import { memo } from 'react';
import { ChevronDown, ChevronRight, Folder, Loader2 } from 'lucide-react';

function isVaultSelected(selectedDestinations, vaultUuid) {
  return selectedDestinations.some((d) => d.vaultUuid === vaultUuid && !d.folderUuid);
}

function isFolderSelected(selectedDestinations, vaultUuid, folderUuid) {
  return selectedDestinations.some(
    (d) => d.vaultUuid === vaultUuid && d.folderUuid === folderUuid,
  );
}

/**
 * @param {object} props
 * @param {Array<{ id: string, title?: string, name?: string }>} props.vaults
 * @param {Record<string, object[]>} props.vaultFolders
 * @param {Set<string>} props.expandedVaults
 * @param {Array<{ vaultUuid: string, folderUuid: string|null, vaultName?: string, folderName?: string }>} props.selectedDestinations
 * @param {Record<string, boolean>} props.loadingFolders
 * @param {(vaultUuid: string) => void} props.onToggleVault
 * @param {(destination: object) => void} props.onToggleDestination
 */
const ChatVaultFolderTree = memo(
  ({
    vaults,
    vaultFolders,
    expandedVaults,
    selectedDestinations,
    loadingFolders,
    onToggleVault,
    onToggleDestination,
  }) => {
    if (!vaults?.length) {
      return (
        <div className="px-4 py-10 text-center text-sm text-content-muted">No vaults available</div>
      );
    }

    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-1 py-2">
        {vaults.map((vault) => {
          const vaultId = vault.id;
          const vaultName = vault.title || vault.name || 'Vault';
          const isExpanded = expandedVaults.has(vaultId);
          const folders = vaultFolders[vaultId] || [];
          const isLoading = loadingFolders[vaultId];

          return (
            <div key={vaultId} className="border-b border-edge/60 py-1 last:border-b-0">
              <div className="flex items-center gap-2 py-1.5">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-content-muted hover:bg-surface-hover-subtle"
                  onClick={() => onToggleVault(vaultId)}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Collapse vault' : 'Expand vault'}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 rounded border-edge text-brand-600 focus:ring-brand-500"
                  checked={isVaultSelected(selectedDestinations, vaultId)}
                  onChange={() =>
                    onToggleDestination({
                      vaultUuid: vault.id,
                      vaultName,
                      folderUuid: null,
                      folderName: 'Root',
                    })
                  }
                  aria-label={`Select root of ${vaultName}`}
                />
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium text-content"
                  onClick={() => onToggleVault(vaultId)}
                >
                  <Folder className="h-4 w-4 shrink-0 text-content-muted" />
                  <span className="truncate">{vaultName}</span>
                </button>
              </div>

              {isExpanded ? (
                <div className="ml-10 border-l border-edge pl-3">
                  {isLoading ? (
                    <div className="flex items-center gap-2 py-2 text-xs text-content-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading folders…
                    </div>
                  ) : folders.length === 0 ? (
                    <p className="py-2 text-xs text-content-muted">No folders in this vault</p>
                  ) : (
                    folders.map((folder) => {
                      const folderId =
                        folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
                      const folderName = folder.name || folder.title || 'Folder';
                      return (
                        <div key={folderId} className="flex items-center gap-2 py-1.5">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded border-edge text-brand-600 focus:ring-brand-500"
                            checked={isFolderSelected(selectedDestinations, vaultId, folderId)}
                            onChange={() =>
                              onToggleDestination({
                                vaultUuid: vault.id,
                                vaultName,
                                folderUuid: folderId,
                                folderName,
                              })
                            }
                            aria-label={`Select folder ${folderName}`}
                          />
                          <span className="flex min-w-0 flex-1 items-center gap-2 text-sm text-content">
                            <Folder className="h-4 w-4 shrink-0 text-content-muted" />
                            <span className="truncate">{folderName}</span>
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  },
);

ChatVaultFolderTree.displayName = 'ChatVaultFolderTree';

export default ChatVaultFolderTree;
